// Calculator Bridge - Cross-calculator data transfer
// Enables seamless transitions between Accumulation → Basic/Advanced → SMSF

import {
    trackCalculatorDestination
} from './utils/funnelAnalytics.js';
import { FinancialConfig } from './FinancialConfig.js';

export class CalculatorBridge {
    /**
     * Transfer from Accumulation to Retirement (Basic or Advanced)
     */
    static accumulationToRetirement(accumulationInputs, accumulationResults, targetCalculator = 'basic') {
        // Calculate years to retirement for inflation adjustment
        const yearsToRetirement = Math.max(accumulationInputs.retirementAge - accumulationInputs.currentAge, 0);
        const inflationFactor = Math.pow(1 + (accumulationInputs.assumedInflation || 2.5) / 100, yearsToRetirement);
        
        // Convert nominal balances to real (today's dollars)
        const nominalUserBalance = accumulationResults?.finalBalances?.user || accumulationInputs.currentSuper;
        const nominalPartnerBalance = accumulationResults?.finalBalances?.partner || accumulationInputs.partnerCurrentSuper || 0;
        
        const realUserBalance = Math.round(nominalUserBalance / inflationFactor);
        const realPartnerBalance = Math.round(nominalPartnerBalance / inflationFactor);
        
        const retirementAge = accumulationInputs.retirementAge || 67;
        const isCouple = accumulationInputs.accumulationRelationshipStatus === 'couple';
        const partnerRetirementAge = accumulationInputs.partnerRetirementAge || 67;

        const transferData = {
            yourAge: retirementAge,
            yourSuper: realUserBalance,
            partnerAge: partnerRetirementAge,
            partnerSuper: realPartnerBalance,
            relationshipStatus: isCouple ? 'couple' : 'single',
            assumedReturn: accumulationInputs.assumedReturn || 7.5,
            assumedInflation: accumulationInputs.assumedInflation || 2.5,
            adminFees: accumulationInputs.adminFees || 52,
            adminFeePercentage: accumulationInputs.adminFeePercentage || 0.10,
            investmentFees: accumulationInputs.investmentFees || 0.5,
            insuranceFees: accumulationInputs.insuranceFees || 0,
            isHomeowner: true,
            includePension: true
        };

        if (targetCalculator === 'advanced') {
            // Additional fields for advanced calculator
            transferData.startYear = new Date().getFullYear();
            transferData.periodLength = 30;
            transferData.sp500 = accumulationInputs.sp500 || 0;
            transferData.ausShares = accumulationInputs.ausShares || 0;
            transferData.intlShares = accumulationInputs.intlShares || 0;
            transferData.bonds = accumulationInputs.bonds || 0;
            transferData.cash = accumulationInputs.cash || 0;
            transferData.frankingCredits = accumulationInputs.frankingCredits || 80;
            
            // Phased Retirement Data
            transferData.yourCurrentAge = accumulationInputs.currentAge;
            transferData.partnerCurrentAge = accumulationInputs.partnerCurrentAge;
            transferData.yourSalary = accumulationInputs.currentSalary || 0;
            transferData.partnerSalary = accumulationInputs.partnerSalary || 0;
            transferData.yourSuperContributions = accumulationInputs.voluntaryContributions || 0;
            transferData.partnerSuperContributions = accumulationInputs.partnerVoluntaryContributions || 0;
            transferData.yourRetirementAge = accumulationInputs.retirementAge;
            transferData.partnerRetirementAge = accumulationInputs.partnerRetirementAge;
            
            // Auto-enable phased retirement if retirement ages differ
            transferData.phasedRetirement = (
              isCouple && accumulationInputs.retirementAge !== accumulationInputs.partnerRetirementAge
            );
        }

        return transferData;
    }

    /**
     * Transfer from Basic to Advanced
     */
    static basicToAdvanced(basicInputs) {
        return {
            relationshipStatus: basicInputs.relationshipStatus || 'single',
            yourAge: basicInputs.yourAge || 65,
            yourSuper: basicInputs.yourSuper || '',
            partnerAge: basicInputs.partnerAge || 65,
            partnerSuper: basicInputs.partnerSuper || '',
            startYear: new Date().getFullYear(),
            periodLength: basicInputs.retirementPeriod || 30,
            isHomeowner: basicInputs.isHomeowner !== undefined ? basicInputs.isHomeowner : true,
            includePension: basicInputs.includePension !== undefined ? basicInputs.includePension : true,
            pensionGrowthRate: basicInputs.pensionGrowthRate || 0.3,
            assumedReturn: basicInputs.assumedReturn || 7.5,
            assumedInflation: basicInputs.assumedInflation || 2.5,
            adminFees: basicInputs.adminFees || 52,
            adminFeePercentage: basicInputs.adminFeePercentage || 0.10,
            investmentFees: basicInputs.investmentFees || 0.5,
            insuranceFees: basicInputs.insuranceFees || '',
            otherTaxableIncome: basicInputs.otherTaxableIncome || '',
            otherNonTaxableIncome: basicInputs.otherNonTaxableIncome || '',
            otherAssets: basicInputs.assessableAssets || '',
            withdrawalStrategy: 'fixed',
            // Set portfolio to balanced default
            sp500: 35,
            ausShares: 15,
            intlShares: 20,
            bonds: 20,
            cash: 10,
            frankingCredits: 80,
            monteCarloTargetIncome: basicInputs.desiredAnnualIncome || ''
        };
    }

    /**
     * Save Basic inputs and open Advanced (hash navigation + GA4).
     */
    static navigateBasicToAdvanced(basicInputs, { eventLabel = 'Basic to Advanced' } = {}) {
        const transferData = CalculatorBridge.basicToAdvanced(basicInputs);
        // Advanced reads via retrieveTransferData / calculatorTransfer (SMSF path).
        // saveTransferData uses a different key — keep both for compatibility.
        CalculatorBridge.storeTransferData(transferData, 'basic', 'advanced');
        CalculatorBridge.saveTransferData('advanced', transferData);
        trackCalculatorDestination({
            source: 'basic',
            destination: 'advanced',
            eventLabel,
            calculatorTier: 'basic'
        });
        if (typeof window !== 'undefined') {
            const nextHash = '#advanced';
            if (window.location.hash !== nextHash) {
                window.location.hash = nextHash;
            } else {
                window.dispatchEvent(new HashChangeEvent('hashchange'));
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    /**
     * Transfer from Advanced/Basic to SMSF
     */
    static retirementToSMSF(retirementInputs, isAdvanced = false) {
        const totalBalance = (retirementInputs.yourSuper || 0) + (retirementInputs.partnerSuper || 0);
        
        return {
            smsfData: {
                fundName: 'My SMSF',
                totalAssets: totalBalance,
                memberCount: retirementInputs.relationshipStatus === 'couple' ? 2 : 1
            },
            memberProfile: {
                age: retirementInputs.yourAge || 65,
                retirementAge: retirementInputs.yourAge || 67
            },
            smsfAdminFees: retirementInputs.adminFees || 52,
            smsfAccountingFees: retirementInputs.adminFees || 0,
            smsfAuditFees: 0,
            ...(isAdvanced ? {
                assetAllocationTargets: {
                    ausShares: retirementInputs.ausShares || 0,
                    intlShares: retirementInputs.intlShares || 0,
                    property: 0,
                    cash: retirementInputs.cash || 0,
                    bonds: retirementInputs.bonds || 0
                }
            } : {})
        };
    }

    /**
     * Save transfer data to LocalStorage for pickup by target calculator
     */
    static saveTransferData(targetCalculator, data) {
        try {
            const transferKey = `calculator_transfer_${targetCalculator}`;
            const transferData = {
                timestamp: new Date().toISOString(),
                source: 'calculator_bridge',
                data: data
            };
            localStorage.setItem(transferKey, JSON.stringify(transferData));
            return true;
        } catch (err) {
            console.error('Failed to save transfer data:', err);
            return false;
        }
    }

    /**
     * Load transfer data from LocalStorage and clear it
     */
    static loadTransferData(calculatorType) {
        try {
            const transferKey = `calculator_transfer_${calculatorType}`;
            const stored = localStorage.getItem(transferKey);
            if (stored) {
                const transferData = JSON.parse(stored);
                // Clear after loading (one-time use)
                localStorage.removeItem(transferKey);
                
                // Check if data is recent (within last 5 minutes)
                const timestamp = new Date(transferData.timestamp);
                const now = new Date();
                const minutesAgo = (now - timestamp) / 1000 / 60;
                
                if (minutesAgo < 5) {
                    return transferData.data;
                }
            }
        } catch (err) {
            console.error('Failed to load transfer data:', err);
        }
        return null;
    }

    /**
     * Check if there's pending transfer data
     */
    static hasTransferData(calculatorType) {
        try {
            const transferKey = `calculator_transfer_${calculatorType}`;
            return localStorage.getItem(transferKey) !== null;
        } catch (err) {
            return false;
        }
    }
    
    /**
     * Transfer from SMSF to Accumulation Calculator
     * For users still working who want to project growth to retirement
     */
    static smsfToAccumulation(smsfInputs) {
        // Pass portfolio percentages as-is from SMSF
        // Infrastructure percentage is passed for display, value goes to otherAlternatives
        
        return {
            // Personal details
            currentAge: smsfInputs.yourAge || 65,
            retirementAge: smsfInputs.yourRetirementAge || 67,
            currentSuper: smsfInputs.yourBalance || smsfInputs.yourSuper || 0,
            currentSalary: smsfInputs.yourSalary || 0,
            
            // Partner details
            accumulationRelationshipStatus: smsfInputs.relationshipStatus || 'single',
            partnerCurrentAge: smsfInputs.partnerAge || '',
            partnerRetirementAge: smsfInputs.partnerRetirementAge || '',
            partnerCurrentSuper: smsfInputs.partnerBalance || smsfInputs.partnerSuper || '',
            partnerSalary: smsfInputs.partnerSalary || '',
            
            // Fees (from SMSF calculator)
            adminFees: smsfInputs.adminFees || 52,
            adminFeePercentage: smsfInputs.adminFeePercentage || 0.10,
            investmentFees: smsfInputs.investmentFees || 0.5,
            insuranceFees: smsfInputs.insuranceFees || 0,
            
            // Asset allocation - pass original percentages from SMSF
            sp500: smsfInputs.sp500 ?? 0,
            ausShares: smsfInputs.ausShares ?? 0,
            intlShares: smsfInputs.intlShares ?? 0,
            bonds: smsfInputs.bonds ?? 0,
            cash: smsfInputs.cash ?? 0,
            infrastructurePct: smsfInputs.infrastructurePct ?? 0, // Pass for display in productive asset breakdown
            frankingCredits: smsfInputs.frankingCredits ?? 80,
            
            // NON-EARNING asset fields (tracked separately, not part of portfolio %)
            residentialPropertyValue: smsfInputs.residentialPropertyValue || 0,
            residentialRentalIncome: smsfInputs.residentialRentalIncome || 0,
            residentialPropertyRentalYield: smsfInputs.residentialPropertyRentalYield || 0,
            residentialPropertyCapitalGrowth: smsfInputs.residentialPropertyCapitalGrowth || 4,
            commercialPropertyValue: smsfInputs.commercialPropertyValue || 0,
            commercialRentalIncome: smsfInputs.commercialRentalIncome || 0,
            commercialPropertyRentalYield: smsfInputs.commercialPropertyRentalYield || 0,
            commercialPropertyCapitalGrowth: smsfInputs.commercialPropertyCapitalGrowth || 4,
            collectiblesValue: smsfInputs.collectiblesValue || 0,
            collectiblesCapitalGrowth: smsfInputs.collectiblesCapitalGrowth || 0,
            
            // Infrastructure mapped to "Other Alternatives" for calculation
            otherAlternativesValue: smsfInputs.infrastructureValue || 0,
            otherAlternativesCapitalGrowth: smsfInputs.infrastructureReturnRate ?? 6,
            
            // NEW: Pass arrays of individual assets from SMSF
            residentialProperties: smsfInputs.residentialProperties || [],
            commercialProperties: smsfInputs.commercialProperties || [],
            alternativeAssets: smsfInputs.alternativeAssets || [],
            collectiblesAssets: smsfInputs.collectiblesAssets || [],
            
            // Contributions
            voluntaryContributions: 0,
            employerSGContributions: 0,
            salaryGrowthRate: 3,
            
            // Returns
            assumedReturn: 7.5,
            assumedInflation: 2.5,
            
            // Transfer flag
            transferFromSMSF: true
        };
    }
    
    /**
     * Transfer from SMSF to Basic Calculator
     * For simple retirement planning
     */
    static smsfToBasic(smsfInputs) {
        return {
            // Personal details
            yourAge: smsfInputs.yourAge || 65,
            yourSuper: smsfInputs.yourBalance || smsfInputs.yourSuper || 0,
            
            // Partner details
            relationshipStatus: smsfInputs.relationshipStatus || 'single',
            partnerAge: smsfInputs.partnerAge || '',
            partnerSuper: smsfInputs.partnerBalance || smsfInputs.partnerSuper || '',
            
            // Retirement parameters
            retirementPeriod: 30,
            targetIncome: '',
            
            // Fees (from SMSF)
            adminFees: smsfInputs.adminFees || 52,
            adminFeePercentage: smsfInputs.adminFeePercentage || 0.10,
            investmentFees: smsfInputs.investmentFees || 0.5,
            insuranceFees: smsfInputs.insuranceFees || 0,
            
            // Other
            isHomeowner: smsfInputs.isHomeowner !== undefined ? smsfInputs.isHomeowner : true,
            includePension: true,
            assumedReturn: 7.5,
            assumedInflation: 2.5,
            pensionGrowthRate: 0.3,
            otherTaxableIncome: smsfInputs.otherIncome || '',
            assessableAssets: smsfInputs.otherAssets || ''
        };
    }
    
    /**
     * Transfer from SMSF to Advanced Calculator
     * For comprehensive retirement planning with all SMSF details
     */
    static smsfToAdvanced(smsfInputs) {
        const isCouple = smsfInputs.relationshipStatus === 'couple';
        
        return {
            // Personal details
            yourAge: smsfInputs.yourAge || 65,
            yourSuper: smsfInputs.yourBalance || smsfInputs.yourSuper || 0,
            yourRetirementAge: smsfInputs.yourRetirementAge || smsfInputs.yourAge || 67,
            yourSalary: smsfInputs.yourSalary || 0,
            yourTbcUsed: smsfInputs.yourTbcUsed || 0,
            
            // Partner details
            relationshipStatus: smsfInputs.relationshipStatus || 'single',
            partnerAge: isCouple ? (smsfInputs.partnerAge || '') : '',
            partnerSuper: isCouple ? (smsfInputs.partnerBalance || smsfInputs.partnerSuper || '') : '',
            partnerRetirementAge: isCouple ? (smsfInputs.partnerRetirementAge || smsfInputs.partnerAge || '') : '',
            partnerSalary: isCouple ? (smsfInputs.partnerSalary || '') : '',
            partnerTbcUsed: isCouple ? (smsfInputs.partnerTbcUsed || '') : '',
            
            // Period - use 1980 as default for historical analysis
            startYear: 1980,
            periodLength: 35,
            targetFinalBalance: 100000,
            
            // Fees (from SMSF)
            adminFees: smsfInputs.adminFees || 52,
            adminFeePercentage: smsfInputs.adminFeePercentage || 0.10,
            investmentFees: smsfInputs.investmentFees || 0.5,
            insuranceFees: smsfInputs.insuranceFees || 0,
            
            // Asset allocation (from SMSF Investment Strategy if available)
            // Productive asset percentages computed from SMSF Asset Valuation Manager
            sp500: smsfInputs.sp500 ?? 0,
            ausShares: smsfInputs.ausShares ?? 0, // CBA shares mapped here
            intlShares: smsfInputs.intlShares ?? 0,
            bonds: smsfInputs.bonds ?? 0,
            cash: smsfInputs.cash ?? 0, // Cash percentage
            frankingCredits: 80,
            
            // Infrastructure (from alternatives) with percentage and value
            infrastructurePct: smsfInputs.infrastructurePct ?? 0,
            infrastructureValue: smsfInputs.infrastructureValue ?? 0,
            infrastructureReturnRate: smsfInputs.infrastructureReturnRate ?? 6, // Default 6% p.a.
            
            // Other income/assets
            // DON'T include rental income here - it's passed separately as commercialRentalIncome/residentialRentalIncome
            // and added to householdIncome in the simulation. Including it here would double-count it.
            otherTaxableIncome: (Number(smsfInputs.otherIncome) || 0),
            otherNonTaxableIncome: '',
            otherAssets: smsfInputs.otherAssets || '',
            annuityAnnualPayment: '',
            definedBenefitPensionAmount: '',
            
            // Alternative Assets from SMSF (if calculated by Asset Valuation Manager)
            transferFromSMSF: true,
            residentialPropertyValue: smsfInputs.residentialPropertyValue ?? '',
            residentialRentalIncome: smsfInputs.residentialRentalIncome ?? '',
            residentialPropertyRentalYield: smsfInputs.residentialPropertyRentalYield ?? '',
            residentialPropertyCapitalGrowth: smsfInputs.residentialPropertyCapitalGrowth ?? '',
            commercialPropertyValue: smsfInputs.commercialPropertyValue ?? '',
            commercialRentalIncome: smsfInputs.commercialRentalIncome ?? '',
            commercialPropertyRentalYield: smsfInputs.commercialPropertyRentalYield ?? '',
            commercialPropertyCapitalGrowth: smsfInputs.commercialPropertyCapitalGrowth ?? '',
            collectiblesValue: smsfInputs.collectiblesValue ?? '',
            collectiblesCapitalGrowth: smsfInputs.collectiblesCapitalGrowth ?? 0,
            
            // Asset details for dynamic UI (shares, infrastructure with names and values)
            assetDetails: smsfInputs.assetDetails ?? null,
            
            // NEW: Pass arrays of individual assets from SMSF
            residentialProperties: smsfInputs.residentialProperties || [],
            commercialProperties: smsfInputs.commercialProperties || [],
            alternativeAssets: smsfInputs.alternativeAssets || [],
            collectiblesAssets: smsfInputs.collectiblesAssets || [],
            
            // Earning vs Non-Earning Aggregation
            earningAssetsValue: smsfInputs.earningAssetsValue ?? '',
            nonEarningAssetsValue: smsfInputs.nonEarningAssetsValue ?? '',
            earningAssetsPct: smsfInputs.earningAssetsPct ?? '',
            nonEarningAssetsPct: smsfInputs.nonEarningAssetsPct ?? '',
            
            // Pension settings
            isHomeowner: smsfInputs.isHomeowner !== undefined ? smsfInputs.isHomeowner : true,
            includePension: true,
            pensionGrowthRate: 0.3,
            
            // Strategy
            withdrawalStrategy: 'fixed',
            
            // Phased retirement (if applicable)
            phasedRetirement: isCouple && smsfInputs.yourRetirementAge && smsfInputs.partnerRetirementAge && 
                               smsfInputs.yourRetirementAge !== smsfInputs.partnerRetirementAge,
            
            // TBC — always the live general cap (not a frozen prior-year figure)
            generalTransferBalanceCap: FinancialConfig.TRANSFER_BALANCE_CAP
        };
    }
    
    /**
     * Store transfer data in localStorage with timestamp
     * Data expires after 5 minutes
     */
    static storeTransferData(data, sourceCalculator, targetCalculator) {
        const transferPackage = {
            data,
            sourceCalculator,
            targetCalculator,
            timestamp: Date.now(),
            expiresAt: Date.now() + (5 * 60 * 1000) // 5 minutes
        };
        
        localStorage.setItem('calculatorTransfer', JSON.stringify(transferPackage));
        console.log(`✅ Transfer data stored: ${sourceCalculator} → ${targetCalculator}`, data);
    }
    
    /**
     * Read transfer metadata without clearing (for analytics).
     */
    static peekTransferPackage(expectedTarget) {
        try {
            const stored = localStorage.getItem('calculatorTransfer');
            if (!stored) return null;
            const transferPackage = JSON.parse(stored);
            if (Date.now() > transferPackage.expiresAt) return null;
            if (expectedTarget && transferPackage.targetCalculator !== expectedTarget) return null;
            return {
                sourceCalculator: transferPackage.sourceCalculator,
                targetCalculator: transferPackage.targetCalculator
            };
        } catch {
            return null;
        }
    }

    /**
     * Retrieve and clear transfer data
     * Returns null if expired or not found
     */
    static retrieveTransferData(expectedTarget) {
        try {
            const stored = localStorage.getItem('calculatorTransfer');
            if (!stored) return null;
            
            const transferPackage = JSON.parse(stored);
            
            // Check expiration
            if (Date.now() > transferPackage.expiresAt) {
                localStorage.removeItem('calculatorTransfer');
                console.log('⚠️ Transfer data expired');
                return null;
            }
            
            // Check target matches
            if (expectedTarget && transferPackage.targetCalculator !== expectedTarget) {
                return null;
            }
            
            // Don't clear immediately - let the calculator decide when to clear
            // This allows multiple components to read the same transfer data
            console.log(`✅ Transfer data retrieved: ${transferPackage.sourceCalculator} → ${transferPackage.targetCalculator}`);
            
            return transferPackage.data;
        } catch (error) {
            console.error('Error retrieving transfer data:', error);
            return null;
        }
    }
    
    // Clear transfer data after it's been used
    static clearTransferData() {
        localStorage.removeItem('calculatorTransfer');
        console.log('🗑️ Transfer data cleared');
    }
}

export default CalculatorBridge;

